import { useEffect, useRef, useState } from 'react';

import { getLocationKey } from '../../support/history';
import { createLocationStore } from './createLocationStore';

const PREFIX = 'nps';

type Serialization<T> = {
  serialize: (input: T) => unknown;
  deserialize: (input: unknown) => T;
};

type Options<T> = {
  serialization?: Serialization<T>;
};

const store = createLocationStore();
const mounted = new Map<string, number>();

export function useNavPersistedState<T>(
  componentKey: string,
  defaultValue: T,
  options?: Options<T>,
) {
  const { serialization } = options ?? {};
  const [locationKey] = useState(() => getLocationKey());
  const [value, setValue] = useState(() => {
    const fromStore = store.get(locationKey, prefix(componentKey));
    if (fromStore !== undefined) {
      return serialization
        ? serialization.deserialize(fromStore)
        : (fromStore as T);
    }
    return defaultValue;
  });
  const valueRef = useRef(value);
  valueRef.current = value;
  useEffect(() => {
    const mountedCount = mounted.get(componentKey) ?? 0;
    if (mountedCount > 0) {
      // eslint-disable-next-line no-console
      console.error(
        `[useNavPersistedState] Multiple components mounted with key "${componentKey}"`,
      );
    }
    mounted.set(componentKey, mountedCount + 1);
    return () => {
      const value = valueRef.current;
      const serialized = serialization ? serialization.serialize(value) : value;
      store.put(locationKey, prefix(componentKey), serialized);
      const mountedCount = mounted.get(componentKey) ?? 0;
      mounted.set(componentKey, mountedCount - 1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [value, setValue] as const;
}

function prefix(key: string) {
  return PREFIX + ':' + key;
}
